import React, { useState } from "react";
import { Upload, Download, Send, Trash2, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pagination } from "@/components/ui/pagination";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";
import { useSellerContract } from "@/hooks/useSellerContract";
import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { ContractUploadDialog } from "@/components/dialogs/ContractUploadDialog";
import { formatDate } from "@/utils/dateUtils";
import {
  AdminSellerContract,
  ManualSellerContractSignatureMethod,
} from "@/types/sellerContract";

type Labels = Record<string, string>;

const MANUAL_METHODS: ManualSellerContractSignatureMethod[] = [
  "physical_id_check",
  "signed_externally",
  "no_signature_required",
  "other",
];

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const SellerContractManagement: React.FC = () => {
  const { language } = useLanguage();
  const sm = (adminLabels(language).economy?.sellerContractManagement ??
    {}) as Labels;
  const {
    useGetAllSellerContracts,
    sendSellerContractAsync,
    requestSignatureAsync,
    markSignatureAsync,
    deleteSellerContractAsync,
    isMarkingSignature,
  } = useSellerContract();
  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();

  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string;
    search?: string;
  }>({});
  const [markTarget, setMarkTarget] = useState<AdminSellerContract | null>(null);
  const [detailContract, setDetailContract] =
    useState<AdminSellerContract | null>(null);
  const [markMethod, setMarkMethod] =
    useState<ManualSellerContractSignatureMethod>("physical_id_check");
  const [markNote, setMarkNote] = useState("");

  const { data, isLoading, error, refetch } = useGetAllSellerContracts(
    page,
    limit,
    appliedFilters.status,
    appliedFilters.search,
  );
  const contracts = data?.contracts ?? [];

  const handleSearch = () => {
    setAppliedFilters({
      status: filterStatus !== "all" ? filterStatus : undefined,
      search: search.trim() || undefined,
    });
    setPage(1);
  };

  const handleClear = () => {
    setFilterStatus("all");
    setSearch("");
    setAppliedFilters({});
    setPage(1);
  };

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      await action();
      toast.success(success);
    } catch (e) {
      toast.error(sm.actionError ?? "Action failed", {
        description: errorMessage(e, ""),
      });
    }
  };

  const handleDownload = async (contract: AdminSellerContract) => {
    if (!contract.document) return;
    try {
      const url = await getDownloadUrlAsync({
        mediaId: contract.document.mediaId,
        expiresIn: 3600,
      });
      await downloadFile(url, contract.document.name);
    } catch (e) {
      toast.error(sm.downloadError ?? "Download failed", {
        description: errorMessage(e, ""),
      });
    }
  };

  const handleDelete = async (contract: AdminSellerContract) => {
    if (!window.confirm(sm.confirmDelete)) return;
    await run(
      () => deleteSellerContractAsync(contract.id),
      sm.deletedToast ?? "Deleted",
    );
  };

  const handleConfirmMark = async () => {
    if (!markTarget) return;
    await run(
      () =>
        markSignatureAsync({
          id: markTarget.id,
          method: markMethod,
          note: markNote.trim() || undefined,
        }),
      sm.markedToast ?? "Marked as signed",
    );
    setMarkTarget(null);
    setMarkNote("");
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <Badge className="bg-green-100 text-green-800">{sm.statusSigned}</Badge>;
      case "requested":
        return <Badge className="bg-amber-100 text-amber-800">{sm.statusRequested}</Badge>;
      case "declined":
        return <Badge className="bg-red-100 text-red-800">{sm.statusDeclined}</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{sm.statusNotRequested}</Badge>;
    }
  };

  const methodLabel = (m: ManualSellerContractSignatureMethod) =>
    ({
      physical_id_check: sm.methodPhysical,
      signed_externally: sm.methodExternal,
      no_signature_required: sm.methodNotRequired,
      other: sm.methodOther,
    })[m];

  const stopRow = (e: React.MouseEvent) => e.stopPropagation();

  const renderActions = (c: AdminSellerContract) => (
    <div className="flex flex-wrap gap-1" onClick={stopRow}>
      <Button
        variant="ghost"
        size="sm"
        title={sm.download}
        disabled={!c.document}
        onClick={() => handleDownload(c)}
      >
        <Download className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        title={sm.send}
        disabled={!c.document}
        onClick={() =>
          run(() => sendSellerContractAsync(c.id), sm.sentToast ?? "Sent")
        }
      >
        <Send className="h-4 w-4" />
      </Button>
      {c.signatureStatus !== "signed" && (
        <>
          <Button
            variant="ghost"
            size="sm"
            title={sm.requestSignature}
            disabled={!c.document}
            onClick={() =>
              run(
                () => requestSignatureAsync(c.id),
                sm.requestedToast ?? "Signature requested",
              )
            }
          >
            <PenLine className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title={sm.markSigned}
            onClick={() => setMarkTarget(c)}
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        title={sm.delete}
        className="text-red-600 hover:text-red-700"
        onClick={() => handleDelete(c)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  const detailRow = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{value || "—"}</span>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{sm.loading}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{sm.error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">{sm.title}</h1>
            <p className="text-muted-foreground">{sm.description}</p>
          </div>
          <Button onClick={() => setUploadOpen(true)} className="gap-2">
            <Upload className="h-4 w-4" />
            {sm.uploadContract}
          </Button>
        </div>

        <Card className="glass-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <CardTitle>
                {sm.contractsCount} ({data?.total ?? contracts.length})
              </CardTitle>
              <div className="flex flex-col md:flex-row md:items-center gap-4 w-full md:w-auto mt-2 md:mt-0">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border bg-card rounded px-2 py-1 w-full md:w-auto"
                >
                  <option value="all">{sm.allStatuses}</option>
                  <option value="not_requested">{sm.statusNotRequested}</option>
                  <option value="requested">{sm.statusRequested}</option>
                  <option value="signed">{sm.statusSigned}</option>
                  <option value="declined">{sm.statusDeclined}</option>
                </select>
                <input
                  type="text"
                  placeholder={sm.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border bg-card rounded px-2 py-1 w-full md:w-64"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSearch}
                    size="sm"
                    disabled={filterStatus === "all" && !search.trim()}
                  >
                    {sm.search}
                  </Button>
                  {(appliedFilters.status || appliedFilters.search) && (
                    <Button onClick={handleClear} variant="outline" size="sm">
                      {sm.clear}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{sm.colNumber}</TableHead>
                    <TableHead>{sm.colVehicle}</TableHead>
                    <TableHead>{sm.colSeller}</TableHead>
                    <TableHead>{sm.colDate}</TableHead>
                    <TableHead>{sm.colStatus}</TableHead>
                    <TableHead>{sm.colActions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setDetailContract(c)}
                    >
                      <TableCell className="font-medium">
                        {c.contractNumber}
                      </TableCell>
                      <TableCell>
                        <div>{c.listing.regNumber}</div>
                        <div className="text-xs text-muted-foreground">
                          {[c.listing.make, c.listing.model, c.listing.year]
                            .filter(Boolean)
                            .join(" ")}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{c.seller.username || c.seller.email}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.seller.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.issuedAt ? formatDate(c.issuedAt) : "-"}
                      </TableCell>
                      <TableCell>{statusBadge(c.signatureStatus)}</TableCell>
                      <TableCell>{renderActions(c)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {contracts.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  {sm.noContracts}
                </div>
              )}
            </div>
            {data && (
              <Pagination
                currentPage={page}
                totalPages={Math.max(1, Math.ceil(data.total / limit))}
                totalItems={data.total}
                itemsPerPage={limit}
                onPageChange={setPage}
                onItemsPerPageChange={() => setPage(1)}
                itemsPerPageOptions={[10, 25, 50]}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <ContractUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        party="seller"
        onSellerContractCreated={() => {
          refetch();
        }}
      />

      <Dialog
        open={!!detailContract}
        onOpenChange={(open) => {
          if (!open) setDetailContract(null);
        }}
      >
        <DialogContent className="max-w-lg px-4">
          <DialogHeader>
            <DialogTitle>
              {sm.detailTitle ?? "Säljarkontrakt"}
              {detailContract
                ? ` #${detailContract.contractNumber}`
                : ""}
            </DialogTitle>
          </DialogHeader>
          {detailContract ? (
            <div className="space-y-3">
              {detailRow(
                sm.detailVehicle ?? "Fordon",
                [
                  detailContract.listing.regNumber,
                  [detailContract.listing.make, detailContract.listing.model, detailContract.listing.year]
                    .filter(Boolean)
                    .join(" "),
                ]
                  .filter(Boolean)
                  .join(" · "),
              )}
              {detailRow(
                sm.detailListing ?? "Annons",
                detailContract.listing.title || detailContract.listingId,
              )}
              {detailRow(
                sm.detailSeller ?? "Säljare",
                detailContract.seller.username || detailContract.seller.email,
              )}
              {detailRow(
                sm.detailEmail ?? "E-post",
                detailContract.seller.email,
              )}
              {detailContract.seller.phoneNumber
                ? detailRow(
                    sm.detailPhone ?? "Telefon",
                    detailContract.seller.phoneNumber,
                  )
                : null}
              {detailRow(
                sm.detailIssued ?? "Utfärdat",
                detailContract.issuedAt
                  ? formatDate(detailContract.issuedAt)
                  : null,
              )}
              {detailRow(
                sm.detailStatus ?? "Status",
                statusBadge(detailContract.signatureStatus),
              )}
              {detailContract.signedAt
                ? detailRow(
                    sm.detailSignedAt ?? "Signerat",
                    formatDate(detailContract.signedAt),
                  )
                : null}
              {detailContract.signedByName
                ? detailRow(
                    sm.detailSignedBy ?? "Signerat av",
                    detailContract.signedByName,
                  )
                : null}
              {detailContract.signatureMethod
                ? detailRow(
                    sm.detailMethod ?? "Metod",
                    detailContract.signatureMethod === "bankid_idura"
                      ? "BankID"
                      : methodLabel(
                          detailContract.signatureMethod as ManualSellerContractSignatureMethod,
                        ),
                  )
                : null}
              {detailContract.signatureNote
                ? detailRow(
                    sm.detailNote ?? "Notering",
                    detailContract.signatureNote,
                  )
                : null}
              {detailRow(
                sm.detailDocument ?? "Dokument",
                detailContract.document ? (
                  <button
                    type="button"
                    className="underline text-left"
                    onClick={() => handleDownload(detailContract)}
                  >
                    {detailContract.document.name}
                  </button>
                ) : (
                  sm.detailNoDocument ?? "Inget dokument"
                ),
              )}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {renderActions(detailContract)}
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() => setDetailContract(null)}
                >
                  {sm.detailClose ?? "Stäng"}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!markTarget}
        onOpenChange={(open) => {
          if (!open) setMarkTarget(null);
        }}
      >
        <DialogContent className="max-w-md px-4">
          <DialogHeader>
            <DialogTitle>{sm.markDialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {sm.methodLabel}
              </label>
              <select
                value={markMethod}
                onChange={(e) =>
                  setMarkMethod(
                    e.target.value as ManualSellerContractSignatureMethod,
                  )
                }
                className="border bg-card rounded px-2 py-2 w-full"
              >
                {MANUAL_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {methodLabel(m)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {sm.noteLabel}
              </label>
              <Input
                value={markNote}
                onChange={(e) => setMarkNote(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMarkTarget(null)}>
                {sm.cancel}
              </Button>
              <Button onClick={handleConfirmMark} disabled={isMarkingSignature}>
                {sm.confirm}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SellerContractManagement;
